import { DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Input, Modal, Space, Table, Tooltip, Tag, message } from "antd";
import axios from "axios";
import { PRIMARY_RED } from "constants/colors";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, Col, Row } from "reactstrap";
import { setBreadcrumbItems } from "../../store/actions";

const RegionalManager = (props) => {
  const [regionalManagers, setRegionalManagers] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRegionalManager, setSelectedRegionalManager] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [expandedRms, setExpandedRms] = useState([]);
  const navigate = useNavigate();

  const toggleExpand = (rmId) => {
    setExpandedRms((prev) =>
      prev.includes(rmId) ? prev.filter((id) => id !== rmId) : [...prev, rmId]
    );
  };

  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const token = authUser?.token;

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const fetchRegionalManager = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASEURL}/regional-manager`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRegionalManagers(response.data);
    } catch (err) {
      message.error("Failed to load regional managers");
    }
  };

  useEffect(() => {
    const breadcrumbItems = [
      { title: "Regional Manager", link: "#" },
      { title: "Regional Manager Listing", link: "#" },
    ];
    props.setBreadcrumbItems("Regional Manager", breadcrumbItems);

    if (token) {
      fetchRegionalManager();
    } else {
      message.error("Session expired, please login again");
    }
  }, [token]);

  const handleDelete = async (regionalManagerId, name) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASEURL}/regional-manager/${regionalManagerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success(`Deleted regional manager: ${name}`);
      fetchRegionalManager();
    } catch (err) {
      message.error("Failed to delete regional manager");
    }
  };

  const filteredData = regionalManagers.filter((rm) => {
    const search = searchText.toLowerCase();
    return (
      rm?.employeeName?.toLowerCase()?.includes(search) ||
      rm?.employeeCode?.toLowerCase()?.includes(search) ||
      rm?.contactNumber?.toString().toLowerCase()?.includes(search) ||
      rm?.email?.toLowerCase()?.includes(search)
    );
  });

  const paginatedData = filteredData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const columns = [
    { title: "Regional Manager Name", dataIndex: "employeeName", key: "employeeName" },
    { title: "Employee Code", dataIndex: "employeeCode", key: "employeeCode" },
    { title: "Contact Number", dataIndex: "contactNumber", key: "contactNumber" },
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Regions",
      key: "regions",
      onCell: () => ({
        style: {
          verticalAlign: 'top',
        },
      }),
      render: (_, record) => {
        const regions = record.regions || (record.region ? [record.region] : []);
        if (!regions || regions.length === 0) return "N/A";

        const regionNames = regions
          .map((r) => {
            if (!r) return "";
            if (typeof r === "object") {
              return r.regionName || r.name || r._id || "";
            }
            return r;
          })
          .filter(Boolean);
        if (regionNames.length === 0) return "N/A";

        if (regionNames.length <= 2) {
          return regionNames.join(", ");
        }

        const isExpanded = expandedRms.includes(record._id);

        if (isExpanded) {
          const firstTwo = regionNames.slice(0, 2).join(", ");
          const remaining = regionNames.slice(2).join(", ");
          return (
            <div>
              <span>{firstTwo}</span>
              <div
                className="expanded-regions"
                style={{ marginTop: 4, lineHeight: 1.5, wordBreak: 'break-word' }}
              >
                {remaining}
              </div>
              <span
                style={{ color: PRIMARY_RED, cursor: "pointer", display: "inline-block", marginTop: 4 }}
                onClick={() => toggleExpand(record._id)}
              >
                View Less
              </span>
            </div>
          );
        } else {
          const displayed = regionNames.slice(0, 2).join(", ");
          const remainingCount = regionNames.length - 2;
          return (
            <span>
              {displayed} +{remainingCount}{" "}
              <span
                style={{ color: PRIMARY_RED, cursor: "pointer", marginLeft: 5 }}
                onClick={() => toggleExpand(record._id)}
              >
                View More
              </span>
            </span>
          );
        }
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => navigate(`/edit-regional-manager/${record._id}`)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              icon={<DeleteOutlined />}
              style={{ color: PRIMARY_RED }}
              onClick={() => {
                setSelectedRegionalManager(record);
                setIsModalVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <React.Fragment>
      <Row>
        <Col xs="12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <input
              type="text"
              placeholder="Search regional manager..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250, padding: "6px 10px" }}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              style={{ backgroundColor: PRIMARY_RED }}
              onClick={() => navigate("/add-regional-manager")}
            >
              Add Regional Manager
            </Button>
          </div>

          <Card>
            <Modal
              title="Delete form"
              visible={isModalVisible}
              onCancel={() => setIsModalVisible(false)}
              footer={[
                <Button key="cancel" onClick={() => setIsModalVisible(false)} style={{ backgroundColor: "#6c757d", color: "white" }}>
                  Close
                </Button>,
                <Button
                  key="delete"
                  type="primary"
                  style={{ backgroundColor: PRIMARY_RED }}
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    handleDelete(selectedRegionalManager._id, selectedRegionalManager.employeeName);
                    setIsModalVisible(false);
                  }}
                >
                  Delete
                </Button>,
              ]}
            >
              <p>Are you sure you want to delete <strong>{selectedRegionalManager?.employeeName}</strong> Regional Manager?</p>
            </Modal>

            <CardBody>
              <Table
                columns={columns}
                dataSource={paginatedData}
                rowKey="_id"
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: filteredData.length,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50"],
                }}
                onChange={handleTableChange}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default connect(null, { setBreadcrumbItems })(RegionalManager);
